import _ from 'lodash';

/**
 * Deeply maps the matching keys of the source object to the new key
 * names specified in the field mapping.
 *
 * Returns a new object with keys matching a mapping field remapped
 * and all others unchanged.
 *
 * @example
 * ```ts
 * const obj = { key: { nestedKey: true }};
 * const simpleMapping = { key: 'newKey' };
 *
 * mapKeysDeep(obj, simpleMapping);
 * // { newKey: { nestedKey: true } }
 *
 * const nestedMapping = { key: ['newKey', { nestedKey: 'newNestedKey' }] };
 * mapKeysDeep(obj, nestedMapping);
 * // { newKey: { newNestedKey: true } }
 * ```
 */
const mapKeysDeep = (
  /* The source object that will have its keys mapped. */
  obj: Record<string, any>,
  /* The field mapping to apply to the source object. */
  mapping: Record<string, any>,
): Record<string, any> => {
  if (!obj || !mapping) {
    return obj;
  }

  if (_.isArray(obj)) {
    return obj.map((objIter: any) => mapKeysDeep(objIter, mapping));
  }
  if (_.isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc, key) => {
      const mappingEntry = mapping[key];
      if (!_.isUndefined(mappingEntry)) {
        if (_.isString(mappingEntry)) {
          acc[mappingEntry] = obj[key];
          return acc;
        }
        // If you are remapping a nested object but also want to remap the key of the property
        // indexing into that nested object, then you can pass the new key name and the nested mapping
        // in a tuple ['newKeyName', {}]
        if (_.isArray(mappingEntry)) {
          acc[mappingEntry[0]] = mapKeysDeep(obj[key], mappingEntry[1]);
          return acc;
        }
        if (_.isPlainObject(mappingEntry)) {
          acc[key] = mapKeysDeep(obj[key], mappingEntry);
          return acc;
        }
        // If you need to a remove a key from the object, you can map it to undefined
      } else if (Object.prototype.hasOwnProperty.call(mapping, key)) {
        return acc;
      }

      acc[key] = obj[key];
      return acc;
    }, {});
  }
  return obj;
};

export default mapKeysDeep;
